// Imagen protegida por token: se pide con el Bearer y se sirve como blob local,
// para que un <img> normal funcione contra un endpoint autenticado.
//
// Nace del incidente del 08-09-2026. Este mismo código, cuando vivía dentro de
// PreInscripcionesPage, revocaba el blob al desmontar y llevaba `token` en las
// dependencias: paginar —o que el token se refrescara solo, cosa que pasa cada
// 45 minutos— disparaba OTRA ronda entera de descargas. En las trazas de S3 se
// veían las mismas imágenes cuatro veces cada una. Cada descarga retiene una
// conexión de BD mientras el backend habla con S3, y el pool son 30: el club se
// quedó a más de 20 segundos (BE #612, BE #613, FE #716).
//
// Estaba encerrado en una pantalla mientras OTRAS repetían el bug con su propia
// copia. Aquí es de todos, y su candado (wave7/imagenAutenticadaNoRepite) fija
// lo único que de verdad importa: lo que ya está traído no se vuelve a pedir.

import { useCallback, useEffect, useRef, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || '';

function buildUrl(path) {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${API_BASE.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
}

/** Añade `?variant=thumb` (o &…) para la lista. El visor usa el path sin variant (full).
 *  La caché del hook clavea por path completo → thumb y full no se pisan. */
export function withImageVariant(path, variant) {
  if (!path || !variant) return path;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (/(?:^|[?&])variant=/.test(path)) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}variant=${encodeURIComponent(variant)}`;
}

// Caché de imágenes ya traídas, por ruta. Antes cada montaje volvía a pedirlo
// todo y el desmontaje revocaba el blob: pasar de página y volver = otra ronda
// completa de peticiones. Cada una retiene una conexión de BD mientras el backend
// habla con S3, y eso fue parte del atasco del 08-09 (BE #612).
//
// Acotada: son fotos de DNI a tamaño completo y esto corre todo el día en la
// tablet del club. Al pasarse, se suelta la más vieja de verdad (revoke).
const IMAGENES = new Map();
const MAX_EN_CACHE = 120;

function recordar(path, url) {
  IMAGENES.set(path, url);
  if (IMAGENES.size > MAX_EN_CACHE) {
    const masVieja = IMAGENES.keys().next().value;
    const suUrl = IMAGENES.get(masVieja);
    if (typeof suUrl === 'string') URL.revokeObjectURL(suUrl);
    IMAGENES.delete(masVieja);
  }
}

// Mismo margen que el IntersectionObserver de abajo. WebKit en CI (Linux) a veces
// retrasa el primer callback aunque el nodo ya esté en pantalla: sin este chequeo
// síncrono la miniatura se queda en placeholder y el candado e2e ve 0 peticiones.
const MARGEN_VISTA_PX = 200;

function yaCercaDelViewport(nodo, margenPx = MARGEN_VISTA_PX) {
  if (!nodo || typeof nodo.getBoundingClientRect !== 'function') return false;
  const r = nodo.getBoundingClientRect();
  // jsdom / nodos sin layout: 0×0 no cuenta como “a la vista”.
  if (r.width <= 0 && r.height <= 0) return false;
  const vw = typeof window !== 'undefined' ? (window.innerWidth || 0) : 0;
  const vh = typeof window !== 'undefined' ? (window.innerHeight || 0) : 0;
  return (
    r.bottom >= -margenPx
    && r.top <= vh + margenPx
    && r.right >= -margenPx
    && r.left <= vw + margenPx
  );
}

/**
 * @param {string|null} path   ruta de la API (o URL absoluta, que se usa tal cual)
 * @param {string} token       el Bearer
 * @param {{diferida?: boolean}} opciones
 *        `diferida` (por defecto true) espera a que el nodo entre en pantalla —
 *        lo que quiere una LISTA. Una ficha que enseña una sola foto ya visible
 *        no debe esperar a nada: se le pasa `false` y no hace falta usar `ref`.
 */
export function useAuthImage(path, token, { diferida = true } = {}) {
  const [src, setSrc] = useState(() => {
    const guardada = IMAGENES.get(path);
    return typeof guardada === 'string' ? guardada : null;
  });
  const [loading, setLoading] = useState(false);
  // La imagen no se pide hasta que su fila se ve. La lista pinta TRES por fila
  // (foto, anverso, reverso): abrirla lanzaba de golpe tres peticiones por cada
  // preinscripción de la página.
  const [aLaVista, setALaVista] = useState(!diferida);
  const nodoRef = useRef(null);
  const observadorRef = useRef(null);

  const medir = useCallback((nodo) => {
    nodoRef.current = nodo;
    // React llama con `null` al desmontar la fila. Sin soltar aquí el observador,
    // cada página de la lista deja ~33 observadores vivos agarrando nodos ya
    // desechados, y la tablet del club no se reinicia en todo el día.
    if (observadorRef.current) {
      observadorRef.current.disconnect();
      observadorRef.current = null;
    }
    if (!nodo || !diferida) return;
    // Sin IntersectionObserver (jsdom, navegadores viejos) no se difiere nada:
    // mejor pedir de más que no enseñar la imagen.
    if (typeof IntersectionObserver === 'undefined') { setALaVista(true); return; }
    const obs = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((e) => e.isIntersecting)) {
          setALaVista(true);
          obs.disconnect();
          if (observadorRef.current === obs) observadorRef.current = null;
        }
      },
      // Un poco antes de entrar en pantalla, para que al hacer scroll ya esté.
      { rootMargin: `${MARGEN_VISTA_PX}px` },
    );
    observadorRef.current = obs;
    obs.observe(nodo);
    // Si ya está (o casi) en viewport, no esperar al callback: en WebKit de CI
    // ese primer disparo a veces llega tarde o no llega a tiempo para el e2e.
    if (yaCercaDelViewport(nodo)) {
      setALaVista(true);
      obs.disconnect();
      if (observadorRef.current === obs) observadorRef.current = null;
    }
  }, [diferida]);

  useEffect(() => {
    // Todas las salidas de abajo apagan `loading`. Si no, cerrar el visor a media
    // descarga lo dejaba encendido para siempre —la limpieza pone `vivo = false` y
    // se traga el `.finally`—, y al reabrir una imagen YA CACHEADA salía una ruleta
    // eterna sobre una imagen que estaba lista.
    if (!path) { setSrc(null); setLoading(false); return; }

    const url = buildUrl(path);
    if (!url) { setSrc(null); setLoading(false); return; }

    // Absolute external URL (e.g. old presigned S3) — use directly
    if (path.startsWith('http://') || path.startsWith('https://')) {
      setSrc(url);
      setLoading(false);
      return;
    }

    const guardada = IMAGENES.get(path);
    if (typeof guardada === 'string') { setSrc(guardada); setLoading(false); return; }
    if (!aLaVista) { setLoading(false); return; }

    let vivo = true;
    const controller = new AbortController();
    setLoading(true);

    // Se guarda la promesa, no solo el resultado: dos miniaturas de la misma ruta
    // —o un remontaje— comparten la petición en vuelo en vez de duplicarla.
    const enVuelo = guardada || fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then((r) => (r.ok ? r.blob() : null))
      .then((blob) => {
        if (!blob || !blob.size) { IMAGENES.delete(path); return null; }
        const blobUrl = URL.createObjectURL(blob);
        recordar(path, blobUrl);
        return blobUrl;
      })
      .catch(() => { IMAGENES.delete(path); return null; });

    if (!guardada) IMAGENES.set(path, enVuelo);

    enVuelo
      .then((blobUrl) => { if (vivo) setSrc(blobUrl); })
      .finally(() => { if (vivo) setLoading(false); });

    return () => {
      vivo = false;
      // NO se revoca el blob ni se aborta: la imagen queda en caché para la
      // próxima vez que se pinte esta fila. Revocarla al desmontar era lo que
      // obligaba a volver a pedirlo todo al paginar.
    };
  }, [path, token, aLaVista]); // eslint-disable-line react-hooks/exhaustive-deps

  return { src, loading, ref: medir };
}

export default useAuthImage;
